/* oxlint-disable typescript/dot-notation, typescript/no-unsafe-return, typescript/no-unsafe-assignment, typescript/no-unsafe-member-access, typescript/no-unsafe-argument, typescript/no-unsafe-call -- query private fields are accessed - it's an antipattern; query cache internals use `any` for heterogeneous query data; generic boundaries enforce safety for callers */
import { hashQueryKey } from './hash-query-key'
import { deepEqual } from 'fast-equals'
import { diff } from '@w/utils'
import { toLogFmt } from '@w/utils'
import { type ReactPromise, ReactPromise as RP } from '@w/utils-fe'
import { createStore, type ReactStore } from '@w/react-store'
import type {
  QueryOptions as QueryOptionsBase,
  MutationOptions as MutationOptionsBase,
  QueryOptionsWithQueryFn,
  MutationInvalidation,
  QueryDependency,
  QueryFunctionContext,
} from './types'
import type { ReactiveBatchQueryOptions } from '@w/utils'
import type { UseInfiniteQueryOptions } from './use-infinite-query'
import type { Tel } from '@w/tel'

export type { InfiniteQueryPageOptions } from './types'

export type QueryData = unknown

export interface QueryCallContext {
  tel?: Tel
}

export interface QueryBatch {
  load<TData>(
    options: QueryOptionsWithQueryFn<TData>,
  ): Promise<{ data: TData; options: QueryOptionsWithQueryFn<TData> }>
}

export interface QueryState {
  data: QueryData
  dataUpdatedAt: Date
  error: Error | undefined
  errorUpdatedAt: Date
  fetchStatus: 'fetching' | 'paused' | 'idle'
  fetchFailureCount: number
  fetchFailureReason: Error | undefined
  status: 'pending' | 'error' | 'success'
  isInvalidated: boolean
  promise: ReactPromise<QueryData> | null
  isFetchingNextPage: boolean
  isFetchingPreviousPage: boolean
}

export interface InfiniteData<TData = unknown, TPageParam = unknown> {
  pages: TData[]
  pageParams: TPageParam[]
}

export interface QueryOptions extends Omit<
  QueryOptionsBase,
  'onError' | 'enabled'
> {
  onError?: (error: Error, query: Query) => void
  enabled?: boolean | ((query: Query) => boolean)
}

export type FetchStatus = 'fetching' | 'paused' | 'idle'

export interface QueryFilters {
  type?: 'all' | 'active' | 'inactive'
  exact?: boolean
  predicate?: (query: Query) => boolean
  queryKey?: unknown[]
  stale?: boolean
  fetchStatus?: FetchStatus
}

export type GlobalQuerySettings = Pick<
  QueryOptions,
  'staleTime' | 'gcTime' | 'onError' | 'refetchOnWindowFocus' | 'retry'
>
export type GlobalMutationSettings = Pick<
  MutationOptions,
  'retry' | 'retryDelay' | 'onError'
>

const globalDefaultQuerySettings: GlobalQuerySettings = {
  staleTime: 1000, // 1 second
  gcTime: 5 * 60 * 1000, // 5 minutes
  refetchOnWindowFocus: true,
}

const globalDefaultMutationSettings: GlobalMutationSettings = {
  retry: false,
}

export class Query {
  queryKey: unknown[]
  queryHash: string
  options: QueryOptions
  observers: Array<(state: QueryState) => void> = []

  private queryDependencies: Query[] = []
  private dependencyObservers = new Set<Map<Query, () => void>>()
  private observerDisposers = new Set<() => void>()
  private acquiredSubscriptions = new Set<() => void>()
  private fetchSubscription: (() => void) | undefined
  private fetchAcquisition: Promise<void> | undefined
  private subscriptionError: Error | undefined
  private activeFetch: ReactPromise<QueryData> | undefined
  private readonly onInvalidate = (): void => {
    void this.markInvalidated('subscription')
  }

  /** Marks the query invalidated; only a query with an enabled observer refetches now. */
  markInvalidated(source: string): Promise<QueryState> {
    if (!this.isDestroyed && this.enabled && this.observers.length > 0) return this.invalidate({ source })
    this.setState({ isInvalidated: true })
    return Promise.resolve(this.state)
  }

  /**
   * Prior art: TanStack Query `setQueryData` (query.ts `setData`): success data from a read made outside this
   * query, so observers update without their own request; clears invalidation and error. Never writes an error.
   * A read already in flight is superseded through its abort signal (TanStack callers `cancelQueries` first):
   * its later result or error is discarded and its joiners resolve with the seeded data.
   */
  setData(data: QueryData): void {
    if (this.isDestroyed) return
    this.abortController?.abort()
    this.abortController = undefined
    const previousStatus = this.state.status
    const unchanged = previousStatus === 'success' && deepEqual(this.state.data, data)
    const value = unchanged ? this.state.data : data
    const promise = this.state.promise
    this.setState({ data: value, dataUpdatedAt: new Date(), error: undefined, status: 'success', isInvalidated: false,
      fetchStatus: 'idle', fetchFailureCount: 0, fetchFailureReason: undefined,
      promise: unchanged && promise?.status === 'fulfilled' && promise.value === value ? promise : RP.resolve(value) }, 'setData')
    if (previousStatus === 'success' && !unchanged) this.queryClient.invalidateQueryDependents(this)
  }

  private abortController: AbortController | undefined
  private gcTimeout: ReturnType<typeof setTimeout> | undefined
  private refetchIntervalId: ReturnType<typeof setInterval> | undefined
  private enabled: boolean = true
  private queryClient: QueryClient
  private isDestroyed: boolean = false
  private pendingFetchReason: string | undefined
  private store: ReactStore<
    QueryState,
    QueryState | ((prev: QueryState) => QueryState)
  >

  constructor(options: QueryOptions, queryClient: QueryClient) {
    this.queryKey = options.queryKey
    this.queryHash = hashQueryKey(options.queryKey)
    this.options = options
    this.queryClient = queryClient

    const status = options.initialData === undefined ? 'pending' : 'success'
    const enabled =
      typeof options.enabled === 'function'
        ? options.enabled(this)
        : (options.enabled ?? true)
    this.enabled = enabled

    const initialState: QueryState = {
      data: options.initialData,
      dataUpdatedAt: new Date(),
      error: undefined,
      errorUpdatedAt: new Date(),
      fetchStatus: 'idle',
      fetchFailureCount: 0,
      fetchFailureReason: undefined,
      status,
      isInvalidated: false,
      promise: status === 'success' ? RP.resolve(options.initialData) : null,
      isFetchingNextPage: false,
      isFetchingPreviousPage: false,
    }

    // Create dedicated store for this query
    this.store = createStore(initialState)

    if (this.queryClient.logging) {
      // oxlint-disable-next-line typescript/no-explicit-any -- log state can contain various types
      const logState: Record<string, any> = {
        queryKey: this.queryKey,
        INIT: true,
        status: initialState.status,
        fetchStatus: initialState.fetchStatus,
      }

      // Only include infinite query fields if this is an infinite query
      if (options.infiniteQueryPageOptions) {
        logState.isFetchingNextPage = initialState.isFetchingNextPage
        logState.isFetchingPreviousPage = initialState.isFetchingPreviousPage
      } else {
        // Non-infinite query - exclude these fields for cleaner logging
      }

      console.info(toLogFmt(logState))
    }
  }

  get state(): QueryState {
    return this.store.getState()
  }

  getStore(): ReactStore<
    QueryState,
    QueryState | ((prev: QueryState) => QueryState)
  > {
    return this.store
  }

  private setState(partial: Partial<QueryState>, reason?: string): void {
    const oldState = this.store.getState()
    const newState = { ...oldState, ...partial }

    this.store.update(newState)

    if (this.queryClient.logging) {
      const patch = diff(oldState, newState)
      if (Object.keys(patch).length > 0) {
        // Include reason from parameter or pendingFetchReason for fetching transitions
        const effectiveReason =
          reason ??
          (patch.fetchStatus === 'fetching'
            ? this.pendingFetchReason
            : undefined)
        if (effectiveReason) {
          console.info(
            toLogFmt({
              queryKey: this.queryKey,
              ...patch,
              reason: effectiveReason,
            }),
          )
          this.pendingFetchReason = undefined
        } else {
          console.info(toLogFmt({ queryKey: this.queryKey, ...patch }))
        }
      }
    }

    this.notify()
  }

  subscribe = (observer: (state: QueryState) => void): (() => void) => {
    if (this.isDestroyed) return () => {}
    const isFirstObserver = this.observers.length === 0
    this.observers.push(observer)
    this.clearGCTimeout()
    const dependencies = new Map<Query, () => void>()
    this.dependencyObservers.add(dependencies)
    this.syncDependencyObservers(dependencies)
    let disposed = false
    let unsubscribe: (() => void) | undefined
    const dispose = (): void => {
      if (disposed) return
      disposed = true
      this.observerDisposers.delete(dispose)
      this.dependencyObservers.delete(dependencies)
      for (const release of dependencies.values()) release()
      dependencies.clear()
      if (unsubscribe) {
        this.acquiredSubscriptions.delete(unsubscribe)
        unsubscribe()
      }
      const index = this.observers.indexOf(observer)
      if (index > -1) this.observers.splice(index, 1)
      if (this.observers.length === 0) {
        if (this.options.subscribe && this.acquiredSubscriptions.size === 0) {
          this.setState({ isInvalidated: true })
        }
        this.scheduleGC()
        this.stopRefetchInterval()
      }
    }
    this.observerDisposers.add(dispose)

    const subscribe = this.options.subscribe
    if (subscribe) {
      const acquire = async (): Promise<void> => {
        try {
          const release = await subscribe(this.onInvalidate)
          if (disposed) {
            release()
            return
          }
          unsubscribe = release
          this.acquiredSubscriptions.add(release)
          this.subscriptionError = undefined
          this.releaseFetchSubscription()
          this.maybeAutoFetch()
        } catch (error) {
          if (disposed) return
          const err = error instanceof Error ? error : new Error(String(error))
          this.subscriptionError = err
          this.setState({ error: err, errorUpdatedAt: new Date(), status: 'error' })
          this.options.onError?.(err, this)
        }
      }
      void acquire()
    }
    if (isFirstObserver && this.enabled) this.maybeAutoFetch()
    if (isFirstObserver && this.enabled) this.maybeStartRefetchInterval()
    return dispose
  }

  setQueryDependencies(queries: Query[]): void {
    this.queryDependencies = queries
    for (const observers of this.dependencyObservers) this.syncDependencyObservers(observers)
  }

  private syncDependencyObservers(observers: Map<Query, () => void>): void {
    for (const [query, dispose] of observers) {
      if (this.queryDependencies.includes(query)) continue
      dispose()
      observers.delete(query)
    }
    for (const query of this.queryDependencies) {
      if (!observers.has(query)) observers.set(query, query.subscribe(() => {}))
    }
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) {
      return
    }

    this.enabled = enabled

    if (this.queryClient?.logging) {
      console.info(toLogFmt({ queryKey: this.queryKey, enabled }))
    }

    if (enabled) {
      this.maybeStartRefetchInterval()
      this.maybeAutoFetch()
    } else {
      this.stopRefetchInterval()
    }
  }

  private maybeAutoFetch(): void {
    if (!this.enabled || this.observers.length === 0) return

    if (this.state.isInvalidated) {
      void this.invalidate()
      return
    }

    // Don't auto-fetch if already fetching
    if (this.state.fetchStatus === 'fetching') {
      return
    }

    // Auto-fetch if pending or stale success
    if (this.state.status === 'pending') {
      void this.fetch('mount')
      return
    }

    if (this.state.status === 'success') {
      const staleTime = this.options.staleTime ?? 0
      if (this.isStale(staleTime)) {
        void this.fetch('stale')
      }
    }
  }

  private maybeStartRefetchInterval(): void {
    if (
      !this.enabled ||
      this.observers.length === 0 ||
      !this.options.refetchInterval
    )
      return

    this.stopRefetchInterval()
    this.refetchIntervalId = setInterval(() => {
      // Only refetch if query is still active (has observers) and enabled and not destroyed
      if (this.enabled && this.observers.length > 0 && !this.isDestroyed) {
        void this.fetch('interval')
      } else {
        // Stop the interval if conditions are no longer met
        this.stopRefetchInterval()
      }
    }, this.options.refetchInterval)
  }

  private stopRefetchInterval(): void {
    if (this.refetchIntervalId) {
      clearInterval(this.refetchIntervalId)
      this.refetchIntervalId = undefined
    }
  }

  private notify(): void {
    for (const observer of this.observers) {
      observer(this.state)
    }
  }

  fetch = (
    reason?: string,
    callCtx?: QueryCallContext,
  ): ReactPromise<QueryData> => {
    if (this.state.fetchStatus === 'fetching' && this.activeFetch) {
      return this.activeFetch
    }

    // Don't fetch if query has been destroyed
    if (this.isDestroyed) {
      return RP.resolve(this.state.data)
    }

    const controller = new AbortController()
    this.abortController = controller

    // Store reason for logging in setState
    this.pendingFetchReason = reason

    // Create promise variable in outer scope so it's accessible in catch block
    let currentPromise: ReactPromise<QueryData>

    const executeFetch = async (): Promise<QueryData> => {
      try {
        while (this.options.subscribe && this.acquiredSubscriptions.size === 0 && !this.isDestroyed) {
          await this.acquireFetchSubscription()
        }
        if (this.isDestroyed || controller.signal.aborted) return this.state.data
        // Commits delivered during acquisition are covered by the read about
        // to start; only commits after this point require a trailing read.
        if (this.state.isInvalidated) this.setState({ isInvalidated: false })
        let data: unknown
        // Handle infinite query
        if (this.options.infiniteQueryPageOptions) {
          const firstPage = await this.options.infiniteQueryPageOptions.queryFn(
            {
              signal: controller.signal,
              queryKey: this.queryKey,
              pageParam: this.options.infiniteQueryPageOptions.initialPageParam,
              tel: callCtx?.tel,
            },
          )
          data = {
            pages: [firstPage],
            pageParams: [
              this.options.infiniteQueryPageOptions.initialPageParam,
            ],
            // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
          }
        } else {
          // Handle standard query
          if (!this.options.queryFn) {
            throw new Error('queryFn is required for non-infinite queries')
          }
          data = await this.options.queryFn({
            signal: controller.signal,
            queryKey: this.queryKey,
            tel: callCtx?.tel,
          })
        }

        if (this.isDestroyed || controller.signal.aborted) return this.state.data
        if (this.subscriptionError) throw this.subscriptionError

        const previousData = this.state.data
        const previousStatus = this.state.status
        // Prior art: TanStack Query structuralSharing (replaceEqualDeep, whole
        // value only): deep-equal rows keep the old reference, so `select` and
        // consumers that compare by reference skip the unchanged data.
        const unchanged = previousStatus === 'success' && deepEqual(previousData, data)
        if (unchanged) data = previousData
        const previousPromise = this.state.promise

        this.setState({
          data,
          dataUpdatedAt: new Date(),
          error: undefined,
          fetchStatus: 'idle',
          fetchFailureCount: 0,
          fetchFailureReason: undefined,
          status: 'success',
          promise: unchanged && previousPromise?.status === 'fulfilled' && previousPromise.value === data ? previousPromise : RP.resolve(data),
        })
        if (previousStatus === 'success' && !unchanged) {
          this.queryClient.invalidateQueryDependents(this)
        }
        return data
      } catch (error) {
        if (this.isDestroyed || controller.signal.aborted) return this.state.data
        const err = error instanceof Error ? error : new Error(String(error))

        this.setState({
          fetchFailureCount: this.state.fetchFailureCount + 1,
          fetchFailureReason: err,
        })

        const shouldRetry = this.shouldRetry(err)
        if (shouldRetry && !this.isDestroyed && !this.subscriptionError) {
          const delay = this.getRetryDelay(err)
          await new Promise<void>((resolve) => {
            setTimeout(resolve, delay)
          })
          if (this.isDestroyed || controller.signal.aborted) return this.state.data
          // Keep the page's successful data (or initial Suspense promise)
          // while recovering. Publishing error would latch an error boundary
          // before the configured retries have a chance to succeed.
          this.activeFetch = undefined
          return this.fetch('retry', callCtx)
        }

        this.setState({
          error: err,
          errorUpdatedAt: new Date(),
          fetchStatus: 'idle',
          status: 'error',
          // Keep promise for suspense (throwOnError=true), but clear it otherwise
          promise: this.options.throwOnError ? currentPromise : null,
        })
        this.options.onError?.(err, this)

        if (this.options.throwOnError) {
          throw err
        }

        // Return undefined when not throwing
        return undefined
      }
    }

    const completion = Promise.withResolvers<QueryData>()
    currentPromise = RP.wrap(completion.promise)

    // If we already have a fulfilled promise (background revalidation case),
    // don't replace it with the pending one - this would cause useSuspenseQuery
    // to suspend unnecessarily. The promise will be updated when fetch completes.
    const shouldKeepExistingPromise =
      this.state.promise?.status === 'fulfilled' ||
      this.state.promise?.status === 'rejected'

    this.activeFetch = currentPromise
    this.setState({
      fetchStatus: 'fetching',
      isInvalidated: false,
      promise: shouldKeepExistingPromise ? this.state.promise : currentPromise,
    })

    const settled = (): void => {
      if (this.activeFetch !== currentPromise) return
      this.activeFetch = undefined
      if (
        this.state.isInvalidated && !this.isDestroyed && !controller.signal.aborted && !this.subscriptionError &&
        (!this.options.subscribe || this.acquiredSubscriptions.size > 0)
      ) {
        void this.fetch('trailing')
      }
    }
    void currentPromise.then(settled, settled)
    void executeFetch().then(completion.resolve, completion.reject)
    return currentPromise
  }

  /**
   * Prior art: TanStack Query QueryObserver.refetch / QueryClient.refetchQueries
   * with cancelRefetch: true cancels an in-flight refetch (when data exists) so
   * its result comes from a read started after the call:
   * https://github.com/TanStack/query/blob/main/packages/query-core/src/queryObserver.ts#L442-L450
   * https://github.com/TanStack/query/blob/main/packages/query-core/src/queryClient.ts#L509-L531
   * https://github.com/TanStack/query/blob/main/packages/query-core/src/query.ts#L663-L682
   * Deliberate difference: wait for the in-flight read, then run a trailing read
   * instead of cancelling. Reject if that required trailing read cannot start.
   */
  async fetchFresh(): Promise<QueryData> {
    if (this.isDestroyed) throw new Error('Fresh query read aborted: query destroyed')
    this.setState({ isInvalidated: true })
    const current = this.state.fetchStatus === 'fetching' && this.activeFetch ? this.activeFetch : this.fetch('fresh')
    let data = await this.waitForFreshFetch(current, this.abortController?.signal)
    // settled() is registered before this waiter and starts the trailing read.
    // Acquisition may instead have cleared invalidation before its read began.
    const trailing = this.activeFetch
    if (trailing && trailing !== current) {
      data = await this.waitForFreshFetch(trailing, this.abortController?.signal)
    } else if (this.state.isInvalidated) {
      // No post-call read cleared invalidation: settled() skipped the trailing
      // read. The old read's result cannot satisfy the freshness boundary.
      throw new Error('Fresh query read did not start')
    }
    return data
  }

  private async waitForFreshFetch(fetch: ReactPromise<QueryData>, signal: AbortSignal | undefined): Promise<QueryData> {
    const aborted = Promise.withResolvers<never>()
    const onAbort = () => { aborted.reject(new Error('Fresh query read aborted')) }
    signal?.addEventListener('abort', onAbort, { once: true })
    try {
      if (this.isDestroyed || signal?.aborted) throw new Error('Fresh query read aborted')
      const data = await Promise.race([fetch, aborted.promise])
      if (this.isDestroyed || signal?.aborted) throw new Error('Fresh query read aborted')
      if (this.subscriptionError) throw this.subscriptionError
      if (this.state.status === 'error') throw this.state.error ?? new Error('Fresh query read failed')
      return data
    } finally {
      signal?.removeEventListener('abort', onAbort)
    }
  }

  private acquireFetchSubscription(): Promise<void> {
    if (this.fetchAcquisition) return this.fetchAcquisition
    const subscribe = this.options.subscribe
    if (!subscribe || this.acquiredSubscriptions.size > 0) return Promise.resolve()
    if (this.observers.length === 0) this.scheduleGC()
    this.fetchAcquisition = (async () => {
      try {
        const release = await subscribe(this.onInvalidate)
        if (this.isDestroyed || this.acquiredSubscriptions.size > 0) {
          release()
          return
        }
        this.fetchSubscription = release
        this.acquiredSubscriptions.add(release)
        this.subscriptionError = undefined
      } catch (error) {
        this.subscriptionError = error instanceof Error ? error : new Error(String(error))
        throw this.subscriptionError
      } finally {
        this.fetchAcquisition = undefined
      }
    })()
    return this.fetchAcquisition
  }

  private releaseFetchSubscription(): void {
    const release = this.fetchSubscription
    if (!release) return
    this.fetchSubscription = undefined
    this.acquiredSubscriptions.delete(release)
    release()
  }

  private shouldRetry(error: Error): boolean {
    const { retry } = this.options

    if (retry === undefined) {
      return this.state.fetchFailureCount <= 3
    }

    if (typeof retry === 'boolean') {
      return retry && this.state.fetchFailureCount <= 3
    }

    if (typeof retry === 'number') {
      return this.state.fetchFailureCount <= retry
    }

    return retry(this.state.fetchFailureCount, error)
  }

  private getRetryDelay(error: Error): number {
    const { retryDelay } = this.options

    if (retryDelay === undefined) {
      return Math.min(1000 * 2 ** (this.state.fetchFailureCount - 1), 30000)
    }

    if (typeof retryDelay === 'number') {
      return retryDelay
    }

    return retryDelay(this.state.fetchFailureCount, error)
  }

  invalidate = async (opts?: {
    source?: string
    mutationId?: string
  }): Promise<QueryState> => {
    this.setState({
      isInvalidated: true,
    })

    // If already fetching, just return current state (don't wait for fetch to complete)
    if (this.state.fetchStatus === 'fetching') {
      return this.state
    }

    // Build reason string with optional mutation ctx
    let reason = opts?.source ?? 'invalidate'
    if (opts?.mutationId) {
      reason = `${reason}:${opts.mutationId}`
    }

    await this.fetch(reason)
    return this.state
  }

  cancel = (): Promise<void> => {
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = undefined
    }

    this.setState({ fetchStatus: 'idle', promise: null }, 'cancel')

    return Promise.resolve()
  }

  isStale(staleTime: number): boolean {
    if (this.state.status !== 'success') {
      return false
    }
    if (this.state.isInvalidated) return true

    const timeSinceUpdate = Date.now() - this.state.dataUpdatedAt.getTime()
    return timeSinceUpdate > staleTime
  }

  hasNextPage = (): boolean => {
    const infiniteOptions = this.options.infiniteQueryPageOptions
    if (!infiniteOptions) {
      return false
    }

    // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
    const data = this.state.data as InfiniteData<any> | undefined
    if (!data || data.pages.length === 0) {
      return false
    }

    const nextParam = infiniteOptions.getNextPageParam(
      data.pages.at(-1),
      data.pages,
    )
    // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no next page"
    return nextParam != null
  }

  hasPreviousPage = (): boolean => {
    const infiniteOptions = this.options.infiniteQueryPageOptions
    if (!infiniteOptions?.getPreviousPageParam) {
      return false
    }

    // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
    const data = this.state.data as InfiniteData<any> | undefined
    if (!data || data.pages.length === 0) {
      return false
    }

    return (
      // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no previous page"
      infiniteOptions.getPreviousPageParam(data.pages[0], data.pages) != null
    )
  }

  fetchNextPage = async (callCtx?: QueryCallContext): Promise<void> => {
    const infiniteOptions = this.options.infiniteQueryPageOptions
    if (!infiniteOptions) {
      return
    }

    // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
    const data = this.state.data as InfiniteData<any> | undefined
    if (!data || data.pages.length === 0) {
      return
    }

    const nextPageParam = infiniteOptions.getNextPageParam(
      data.pages.at(-1),
      data.pages,
    )
    // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no next page"
    if (nextPageParam == null) {
      return
    }

    if (this.state.isFetchingNextPage) {
      return
    }

    this.setState({ isFetchingNextPage: true }, 'fetchNextPage')

    try {
      const abortController = new AbortController()
      const nextPage = await infiniteOptions.queryFn({
        signal: abortController.signal,
        queryKey: this.queryKey,
        pageParam: nextPageParam,
        tel: callCtx?.tel,
      })

      this.setState(
        {
          data: {
            pages: [...data.pages, nextPage],
            pageParams: [...data.pageParams, nextPageParam],
            // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
          },
          isFetchingNextPage: false,
        },
        'fetchNextPage:success',
      )
    } catch (error) {
      this.setState({ isFetchingNextPage: false }, 'fetchNextPage:error')
      throw error
    }
  }

  fetchPreviousPage = async (callCtx?: QueryCallContext): Promise<void> => {
    const infiniteOptions = this.options.infiniteQueryPageOptions
    if (!infiniteOptions?.getPreviousPageParam) {
      return
    }

    // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
    const data = this.state.data as InfiniteData<any> | undefined
    if (!data || data.pages.length === 0) {
      return
    }

    const previousPageParam = infiniteOptions.getPreviousPageParam(
      data.pages[0],
      data.pages,
    )
    // oxlint-disable-next-line eqeqeq -- intentional loose check: null or undefined both signal "no previous page"
    if (previousPageParam == null) {
      return
    }

    if (this.state.isFetchingPreviousPage) {
      return
    }

    this.setState({ isFetchingPreviousPage: true }, 'fetchPreviousPage')

    try {
      const abortController = new AbortController()
      const previousPage = await infiniteOptions.queryFn({
        signal: abortController.signal,
        queryKey: this.queryKey,
        pageParam: previousPageParam,
        tel: callCtx?.tel,
      })

      this.setState(
        {
          data: {
            pages: [previousPage, ...data.pages],
            pageParams: [previousPageParam, ...data.pageParams],
            // oxlint-disable-next-line typescript/no-explicit-any -- InfiniteData generic parameter unknown at runtime
          },
          isFetchingPreviousPage: false,
        },
        'fetchPreviousPage:success',
      )
    } catch (error) {
      this.setState(
        { isFetchingPreviousPage: false },
        'fetchPreviousPage:error',
      )
      throw error
    }
  }

  private scheduleGC(): void {
    this.clearGCTimeout()
    const gcTime =
      this.options.gcTime ?? globalDefaultQuerySettings.gcTime ?? 5 * 60 * 1000

    this.gcTimeout = setTimeout(() => {
      this.queryClient.garbageCollectQuery(this)
    }, gcTime)
  }

  private clearGCTimeout(): void {
    if (this.gcTimeout) {
      clearTimeout(this.gcTimeout)
      this.gcTimeout = undefined
    }
  }

  destroy(): void {
    this.isDestroyed = true
    for (const dispose of this.observerDisposers) dispose()
    this.releaseFetchSubscription()
    // Abort any in-flight requests
    if (this.abortController) {
      this.abortController.abort()
      this.abortController = undefined
    }
    this.clearGCTimeout()
    this.stopRefetchInterval()
    // Don't call cancel() - it would set promise=null which causes issues
    // if components are still subscribed to the store during cleanup
    // Terminal notification: a destroyed query never emits again, so a mounted reader would keep
    // its last value forever. A new state identity re-renders it; useQuery then re-binds through
    // syncQueryOptions to a fresh query on the client (TanStack: observed queries reset, not vanish).
    this.store.update({ ...this.store.getState() })
  }
}

export interface MutationState<TData = unknown, TVariables = unknown> {
  data: TData | undefined
  error: Error | undefined
  failureCount: number
  failureReason: Error | undefined
  status: 'idle' | 'pending' | 'error' | 'success'
  isPaused: boolean
  variables: TVariables | undefined
  submittedAt: Date | undefined
}

export interface MutationOptions<
  TData = unknown,
  TVariables = unknown,
> extends Omit<
  MutationOptionsBase<TData, TVariables>,
  'onMutate' | 'onSuccess' | 'onError' | 'onSettled'
> {
  onMutate?: (variables: TVariables) => Promise<void> | void
  onSuccess?: (
    data: TData,
    variables: TVariables,
    mutation: Mutation<TData, TVariables>,
  ) => Promise<void> | void
  onError?: (
    error: Error,
    variables: TVariables,
    mutation: Mutation<TData, TVariables>,
  ) => Promise<void> | void
  onSettled?: (
    data: TData | undefined,
    error: Error | undefined,
    variables: TVariables,
    mutation: Mutation<TData, TVariables>,
  ) => Promise<void> | void
}

export class Mutation<TData = unknown, TVariables = unknown> {
  id: string
  options: MutationOptions<TData, TVariables>
  observers: Array<(state: MutationState<TData, TVariables>) => void> = []
  private queryClient: QueryClient
  private pendingPromise: Promise<TData> | null = null
  private latestMutationId = 0
  private store: ReactStore<
    MutationState<TData, TVariables>,
    | MutationState<TData, TVariables>
    | ((
        prev: MutationState<TData, TVariables>,
      ) => MutationState<TData, TVariables>)
  >

  constructor(
    id: string,
    options: MutationOptions<TData, TVariables>,
    queryClient: QueryClient,
  ) {
    this.id = id
    this.options = options
    this.queryClient = queryClient

    const initialState: MutationState<TData, TVariables> = {
      data: undefined,
      error: undefined,
      failureCount: 0,
      failureReason: undefined,
      status: 'idle',
      isPaused: false,
      variables: undefined,
      submittedAt: undefined,
    }

    // Create dedicated store for this mutation
    this.store = createStore(initialState)

    if (this.queryClient.logging) {
      console.info(
        toLogFmt({
          mutationId: this.id,
          INIT: true,
          status: initialState.status,
        }),
      )
    }
  }

  get state(): MutationState<TData, TVariables> {
    return this.store.getState()
  }

  getStore(): ReactStore<
    MutationState<TData, TVariables>,
    | MutationState<TData, TVariables>
    | ((
        prev: MutationState<TData, TVariables>,
      ) => MutationState<TData, TVariables>)
  > {
    return this.store
  }

  private setState(
    partial: Partial<MutationState<TData, TVariables>>,
    reason?: string,
  ): void {
    const oldState = this.store.getState()
    const newState = { ...oldState, ...partial }
    this.store.update(newState)

    if (this.queryClient.logging) {
      const patch = diff(oldState, newState)
      if (Object.keys(patch).length > 0) {
        if (reason) {
          console.info(toLogFmt({ mutationId: this.id, ...patch, reason }))
        } else {
          console.info(toLogFmt({ mutationId: this.id, ...patch }))
        }
      }
    }

    this.notify()
  }

  subscribe(
    observer: (state: MutationState<TData, TVariables>) => void,
  ): () => void {
    this.observers.push(observer)
    return () => {
      const index = this.observers.indexOf(observer)
      if (index > -1) {
        this.observers.splice(index, 1)
      }
    }
  }

  private notify(): void {
    for (const observer of this.observers) {
      observer(this.state)
    }
  }

  mutate = (variables: TVariables): void => {
    this.mutateAsync(variables).catch(() => {
      // Error is handled in mutation state
    })
  }

  mutateAsync = (variables: TVariables): Promise<TData> => {
    // Assign an ID to this mutation call
    const mutationId = ++this.latestMutationId

    // If mutation already pending, queue this call
    if (this.pendingPromise) {
      return this.pendingPromise.then(() => this.mutateAsync(variables))
    }

    const execute = async (): Promise<TData> => {
      // Helper to check if this mutation is stale
      const isStale = () => mutationId !== this.latestMutationId

      // Retry loop within same execution ctx
      let isRetry = false
      while (true) {
        this.setState(
          {
            status: 'pending',
            variables,
            submittedAt: new Date(),
            isPaused: false,
          },
          isRetry ? 'retry' : 'mutate',
        )

        try {
          if (this.options.onMutate) {
            await this.options.onMutate(variables)
          }

          const data = await this.options.mutationFn(variables)

          // Only update state if not stale
          if (!isStale()) {
            this.setState(
              {
                data,
                error: undefined,
                failureCount: 0,
                failureReason: undefined,
                status: 'success',
              },
              'success',
            )
          }

          // Skip callbacks if stale
          if (!isStale()) {
            if (this.options.onSuccess) {
              await this.options.onSuccess(data, variables, this)
            }

            if (this.options.onSettled) {
              await this.options.onSettled(data, undefined, variables, this)
            }

            // Auto-invalidate dependent queries
            if (this.queryClient && this.options.invalidates) {
              this.queryClient.invalidateMutationDependencies(
                resolveInvalidations(this.options.invalidates, variables),
                this.id,
              )
            }
          }

          return data
        } catch (error) {
          const err = error as Error

          // Only update state if not stale
          if (!isStale()) {
            this.setState(
              {
                error: err,
                failureCount: this.state.failureCount + 1,
                failureReason: err,
                status: 'error',
              },
              'error',
            )
          }

          // Skip error callbacks if stale
          if (!isStale()) {
            if (this.options.onError) {
              await this.options.onError(err, variables, this)
            }

            if (this.options.onSettled) {
              await this.options.onSettled(undefined, err, variables, this)
            }
          }

          const shouldRetry = this.shouldRetry(err)
          if (shouldRetry) {
            const delay = this.getRetryDelay(err)
            await new Promise<void>((resolve) => {
              setTimeout(resolve, delay)
            })
            // Continue loop to retry
            isRetry = true
            continue
          }

          if (this.options.throwOnError) {
            throw err
          }

          throw err
        }
      }
    }

    this.pendingPromise = execute().finally(() => {
      this.pendingPromise = null
    })

    return this.pendingPromise
  }

  private shouldRetry(error: Error): boolean {
    const { retry } = this.options

    if (retry === undefined) {
      return false
    }

    if (typeof retry === 'boolean') {
      return retry && this.state.failureCount <= 3
    }

    if (typeof retry === 'number') {
      return this.state.failureCount <= retry
    }

    return retry(this.state.failureCount, error)
  }

  private getRetryDelay(error: Error): number {
    const { retryDelay } = this.options

    if (retryDelay === undefined) {
      return Math.min(1000 * 2 ** (this.state.failureCount - 1), 30000)
    }

    if (typeof retryDelay === 'number') {
      return retryDelay
    }

    return retryDelay(this.state.failureCount, error)
  }

  reset = (): void => {
    this.setState(
      {
        data: undefined,
        error: undefined,
        failureCount: 0,
        failureReason: undefined,
        status: 'idle',
        isPaused: false,
        variables: undefined,
        submittedAt: undefined,
      },
      'reset',
    )
  }

  destroy(): void {
    this.observers = []
  }
}

export class QueryClient {
  private queries = new Map<string, Query>()
  private mutations = new Map<string, Mutation>()
  private dependencyAndQuery = new Map<string, Set<Query>>()
  private runtimeQueryDependencies = new Map<string, Set<string>>()
  private executingBatches = new Set<string>()
  private defaultOptions: GlobalQuerySettings
  private defaultMutationOptions: GlobalMutationSettings
  private windowFocusUnsubscribe?: () => void
  private windowFocusListenerSetup = false
  logging: boolean

  constructor(defaultOptions?: {
    queries?: Partial<GlobalQuerySettings>
    mutations?: Partial<GlobalMutationSettings>
    logging?: boolean
  }) {
    this.defaultOptions = {
      ...globalDefaultQuerySettings,
      ...defaultOptions?.queries,
    }
    this.defaultMutationOptions = {
      ...globalDefaultMutationSettings,
      ...defaultOptions?.mutations,
    }
    this.logging = defaultOptions?.logging ?? false
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- log data can contain various types
  private log(prefix: string, data: Record<string, any>): void {
    if (this.logging) {
      console.info(`${prefix} ${toLogFmt(data)}`)
    }
  }

  private ensureWindowFocusListener(): void {
    if (this.windowFocusListenerSetup) return
    this.windowFocusListenerSetup = true

    const handleFocus = () => {
      const queries = Array.from(this.queries.values())
      for (const query of queries) {
        // Only refetch if query has active observers (is mounted)
        if (query.observers.length > 0 && query.options.refetchOnWindowFocus) {
          const staleTime =
            query.options.staleTime ?? this.defaultOptions.staleTime ?? 0
          const isStale = query.isStale(staleTime)
          if (isStale) {
            void query.fetch('windowFocus')
          }
        }
      }
    }

    if (typeof window !== 'undefined') {
      window.addEventListener('focus', handleFocus)
      this.windowFocusUnsubscribe = () => {
        window.removeEventListener('focus', handleFocus)
      }
    }
  }

  getQueryHash(queryKey: unknown[]): string {
    return hashQueryKey(queryKey)
  }

  private mergeOptions(options: QueryOptions): QueryOptions {
    return {
      ...options,
      staleTime: options.staleTime ?? this.defaultOptions.staleTime,
      gcTime: options.gcTime ?? this.defaultOptions.gcTime,
      onError: options.onError ?? this.defaultOptions.onError,
      retry: options.retry ?? this.defaultOptions.retry,
      refetchOnWindowFocus:
        options.refetchOnWindowFocus ??
        this.defaultOptions.refetchOnWindowFocus,
    }
  }

  syncQueryOptions(options: QueryOptions, callCtx?: QueryCallContext): Query {
    const queryHash = this.getQueryHash(options.queryKey)
    let query = this.queries.get(queryHash)

    if (query) {
      // Evaluate new enabled value
      const newEnabledValue =
        typeof options.enabled === 'function'
          ? options.enabled(query)
          : (options.enabled ?? true)

      // Compare options excluding enabled function (compare rest of options)
      const { enabled: _oldEnabled, ...oldOptionsWithoutEnabled } =
        query.options
      const { enabled: _newEnabled, ...newOptionsWithoutEnabled } = options

      const mergedNewOptions = this.mergeOptions(newOptionsWithoutEnabled)
      const optionsChanged = !deepEqual(
        oldOptionsWithoutEnabled,
        mergedNewOptions,
      ) // queryFn (and similar) re-creation on re-render will lead to this be always true / false - that's normal, just update options
      const enabledChanged = query['enabled'] !== newEnabledValue

      if (optionsChanged || enabledChanged) {
        // Update query options
        query.options = this.mergeOptions(options)
        if (enabledChanged) {
          query.setEnabled(newEnabledValue)
        }
      }

      // Update dependencies
      const oldDepends = oldOptionsWithoutEnabled.depends ?? []
      const newDepends = mergedNewOptions.depends ?? []
      if (!deepEqual(oldDepends, newDepends)) {
        // Remove old dependencies
        for (const dep of oldDepends) {
          this.unregisterQueryDependency(dep, query)
        }
        // Add new dependencies
        for (const dep of newDepends) {
          this.registerQueryDependency(dep, query)
        }
      }
    } else {
      const mergedOptions = this.mergeOptions(options)
      query = new Query(mergedOptions, this)
      // Set initial enabled state from options
      const enabled =
        typeof mergedOptions.enabled === 'function'
          ? mergedOptions.enabled(query)
          : (mergedOptions.enabled ?? true)
      query['enabled'] = enabled
      this.queries.set(queryHash, query)

      // Start after map insert so recursive loads can find same query.
      if (enabled && query.state.status === 'pending') {
        void query.fetch('init', callCtx)
      }

      // Register dependencies
      if (mergedOptions.depends) {
        for (const dep of mergedOptions.depends) {
          this.registerQueryDependency(dep, query)
        }
      }

      // Set up window focus listener if this query needs it (delayed to avoid race conditions)
      if (mergedOptions.refetchOnWindowFocus) {
        queueMicrotask(() => {
          this.ensureWindowFocusListener()
        })
      }
    }

    return query
  }

  private getOrCreateQuery(
    options: QueryOptions,
    callCtx?: QueryCallContext,
  ): Query {
    return this.syncQueryOptions(options, callCtx)
  }

  fetchQuery<TData>(options: QueryOptionsWithQueryFn<TData>, callCtx?: QueryCallContext): Promise<TData>
  fetchQuery(options: QueryOptions, callCtx?: QueryCallContext): Promise<QueryData>
  fetchQuery(options: QueryOptions, callCtx?: QueryCallContext): Promise<QueryData> {
    const query = this.getOrCreateQuery(options, callCtx)
    if (
      query.options.subscribe &&
      query['acquiredSubscriptions'].size > 0 &&
      query.state.status === 'success' &&
      query.state.fetchStatus === 'idle' &&
      !query.state.isInvalidated &&
      !query.isStale(query.options.staleTime ?? 0)
    ) return Promise.resolve(query.state.data)
    return query.fetch('fetchQuery', callCtx)
  }

  fetchFresh(queryKey: unknown[]): Promise<QueryData> {
    const query = this.getQuery(queryKey)
    if (!query) return Promise.reject(new Error('Fresh query read requires an existing query'))
    return query.fetchFresh()
  }

  batch<TData>(
    options: ReactiveBatchQueryOptions<TData, QueryFunctionContext>,
  ): QueryOptionsWithQueryFn<TData>
  batch<TData>(
    key: unknown[],
    fn: (batch: QueryBatch) => Promise<{
      data: TData
      options: QueryOptionsWithQueryFn<TData>
    }>,
  ): QueryOptionsWithQueryFn<TData>
  batch<TData>(
    keyOrOptions:
      | unknown[]
      | ReactiveBatchQueryOptions<TData, QueryFunctionContext>,
    fn?: (batch: QueryBatch) => Promise<{
      data: TData
      options: QueryOptionsWithQueryFn<TData>
    }>,
  ): QueryOptionsWithQueryFn<TData> {
    const fromOptions = !Array.isArray(keyOrOptions)
    const queryKey = fromOptions
      ? keyOrOptions.queryKey
      : ['batch', ...keyOrOptions]
    const queryHash = this.getQueryHash(queryKey)
    const restOptions = fromOptions
      ? ({ ...keyOrOptions, queryFn: undefined } as Omit<
          QueryOptionsWithQueryFn<TData>,
          'queryFn'
        >)
      : {}
    const batchQueryFn: (batch: QueryBatch) => Promise<TData> = fromOptions
      ? (batch) => keyOrOptions.queryFn(batch)
      : async (batch) => {
          const result = await fn?.(batch)
          if (!result) {
            throw new Error('query batch function is required')
          }
          return result.data
        }

    return {
      ...(restOptions as Omit<QueryOptionsWithQueryFn<TData>, 'queryFn'>),
      queryKey,
      queryFn: async ({ tel }) => {
        if (this.executingBatches.has(queryHash)) {
          throw new Error('query batch cycle detected')
        }

        const dependencyHashes = new Set<string>()
        const batch: QueryBatch = {
          load: <TInnerData>(options: QueryOptionsWithQueryFn<TInnerData>) => {
            const dependencyHash = this.getQueryHash(options.queryKey)
            if (
              dependencyHash === queryHash ||
              this.executingBatches.has(dependencyHash)
            ) {
              throw new Error('query batch cycle detected')
            }
            dependencyHashes.add(dependencyHash)
            return this.load(options, { tel })
          },
        }

        this.executingBatches.add(queryHash)
        try {
          const data = await batchQueryFn(batch)
          const query = this.getQuery(queryKey)
          if (query) {
            this.replaceRuntimeQueryDependencies(query, dependencyHashes)
          }
          return data
        } finally {
          this.executingBatches.delete(queryHash)
        }
      },
    }
  }

  // oxlint-disable-next-line require-await -- async required by API contract; early return branch returns non-promise
  async ensureQueryData(options: QueryOptions): Promise<QueryData> {
    const query = this.getOrCreateQuery(options)
    const staleTime = options.staleTime ?? this.defaultOptions.staleTime ?? 0

    // Return cached data immediately if available (even if stale)
    if (query.state.status === 'success') {
      const age = Date.now() - query.state.dataUpdatedAt.getTime()

      if (query.isStale(staleTime)) {
        this.log('QC', {
          action: 'cache-hit',
          queryKey: options.queryKey,
          reason: 'stale',
          age,
        })
        // Trigger background revalidation - don't await
        void query.fetch('staleRevalidation')
      } else {
        this.log('QC', {
          action: 'cache-hit',
          queryKey: options.queryKey,
          reason: 'fresh',
          age,
        })
      }

      return query.state.data
    }

    // No cached data - must await fetch
    this.log('QC', {
      action: 'cache-miss',
      queryKey: options.queryKey,
      reason: 'pending',
    })
    return query.fetch('ensureQueryData')
  }

  prefetchQuery(options: QueryOptions, callCtx?: QueryCallContext): QueryData {
    const query = this.getOrCreateQuery(options, callCtx)
    const staleTime = options.staleTime ?? this.defaultOptions.staleTime ?? 0

    if (query.state.status !== 'success' || query.isStale(staleTime)) {
      const reason = query.state.status === 'success' ? 'stale' : 'pending'
      const age =
        query.state.status === 'success'
          ? Date.now() - query.state.dataUpdatedAt.getTime()
          : 0
      this.log('QC', {
        action: 'prefetch',
        queryKey: options.queryKey,
        reason,
        age,
      })
      void query.fetch('prefetch', callCtx)
    } else {
      const age = Date.now() - query.state.dataUpdatedAt.getTime()
      this.log('QC', {
        action: 'prefetch-skipped',
        queryKey: options.queryKey,
        reason: 'fresh',
        age,
      })
    }

    return query.state.data
  }

  async ensureInfiniteQueryData<TData, TPageParam = unknown>(
    options: UseInfiniteQueryOptions<TData, TPageParam>,
  ): Promise<InfiniteData<TData, TPageParam>> {
    const queryOptions: QueryOptions = {
      queryKey: options.queryKey,
      infiniteQueryPageOptions: {
        queryFn: options.queryFn,
        initialPageParam: options.initialPageParam,
        getNextPageParam: options.getNextPageParam,
        getPreviousPageParam: options.getPreviousPageParam,
      },
      staleTime: options.staleTime,
      gcTime: options.gcTime,
      enabled: options.enabled,
      retry: options.retry,
      retryDelay: options.retryDelay,
      refetchOnWindowFocus: options.refetchOnWindowFocus,
      refetchInterval: options.refetchInterval,
      meta: options.meta,
      depends: options.depends,
      subscribe: options.subscribe,
    }

    const data = await this.ensureQueryData(queryOptions)
    return data as InfiniteData<TData, TPageParam>
  }

  prefetchInfiniteQuery<TData, TPageParam = unknown>(
    options: UseInfiniteQueryOptions<TData, TPageParam>,
  ): InfiniteData<TData, TPageParam> | undefined {
    const queryOptions: QueryOptions = {
      queryKey: options.queryKey,
      infiniteQueryPageOptions: {
        queryFn: options.queryFn,
        initialPageParam: options.initialPageParam,
        getNextPageParam: options.getNextPageParam,
        getPreviousPageParam: options.getPreviousPageParam,
      },
      staleTime: options.staleTime,
      gcTime: options.gcTime,
      enabled: options.enabled,
      retry: options.retry,
      retryDelay: options.retryDelay,
      refetchOnWindowFocus: options.refetchOnWindowFocus,
      refetchInterval: options.refetchInterval,
      meta: options.meta,
      depends: options.depends,
    }

    const data = this.prefetchQuery(queryOptions)
    return data as InfiniteData<TData, TPageParam> | undefined
  }

  async load<TData>(
    options: QueryOptionsWithQueryFn<TData>,
    callCtx?: QueryCallContext,
  ): Promise<{ data: TData; options: QueryOptionsWithQueryFn<TData> }> {
    const query = this.getOrCreateQuery(options, callCtx)
    const staleTime = options.staleTime ?? this.defaultOptions.staleTime ?? 0
    const data =
      query.state.status === 'success' && !query.isStale(staleTime)
        ? query.state.data
        : await query.fetch('load', callCtx)
    return { data: data as TData, options }
  }

  preload<TData>(
    options: QueryOptionsWithQueryFn<TData>,
    callCtx?: QueryCallContext,
  ): {
    data: Promise<TData>
    options: QueryOptionsWithQueryFn<TData>
  } {
    const cachedData = this.prefetchQuery(options, callCtx)

    if (cachedData !== undefined) {
      return { data: Promise.resolve(cachedData as TData), options }
    }

    // If no cached data, wait for the fetch triggered by prefetchQuery
    // oxlint-disable-next-line typescript/no-non-null-assertion -- prefetchQuery ensures query exists
    const query = this.getQuery(options.queryKey)!
    const promise =
      query.state.promise?.then((data) => data as TData) ??
      Promise.resolve(undefined as TData)
    return { data: promise, options }
  }

  async loadInfinite<TData, TPageParam = unknown>(
    options: UseInfiniteQueryOptions<TData, TPageParam>,
  ): Promise<{
    data: InfiniteData<TData, TPageParam>
    options: UseInfiniteQueryOptions<TData, TPageParam>
  }> {
    const data = await this.ensureInfiniteQueryData(options)
    return { data, options }
  }

  preloadInfinite<TData, TPageParam = unknown>(
    options: UseInfiniteQueryOptions<TData, TPageParam>,
  ): {
    data: Promise<InfiniteData<TData, TPageParam>>
    options: UseInfiniteQueryOptions<TData, TPageParam>
  } {
    const cachedData = this.prefetchInfiniteQuery(options)

    if (cachedData !== undefined) {
      return { data: Promise.resolve(cachedData), options }
    }

    // If no cached data, wait for the fetch triggered by prefetchInfiniteQuery
    // oxlint-disable-next-line typescript/no-non-null-assertion -- prefetchInfiniteQuery ensures query exists
    const query = this.getQuery(options.queryKey)!
    const promise =
      query.state.promise?.then(
        (data) => data as InfiniteData<TData, TPageParam>,
      ) ??
      Promise.resolve({ pages: [], pageParams: [] } as InfiniteData<
        TData,
        TPageParam
      >)
    return { data: promise, options }
  }

  async invalidateQueries(filters: QueryFilters = {}): Promise<Query[]> {
    const queriesToInvalidate = this.getQueries(filters)
    const promises = queriesToInvalidate.map((query) => query.markInvalidated('invalidate'))
    await Promise.all(promises)
    return queriesToInvalidate
  }

  removeQueries(filters: QueryFilters = {}): Query[] {
    const queriesToRemove = this.getQueries(filters)
    for (const query of queriesToRemove) {
      this.removeQueryFromDependencies(query)
      this.queries.delete(query.queryHash)
      query.destroy()
    }
    return queriesToRemove
  }

  private getQueries(filters: QueryFilters): Query[] {
    const queries = Array.from(this.queries.values())

    return queries.filter((query) => {
      // Filter by type
      if (filters.type) {
        const isActive = query.observers.length > 0
        if (filters.type === 'active' && !isActive) return false
        if (filters.type === 'inactive' && isActive) return false
      }

      // Filter by queryKey
      if (filters.queryKey) {
        if (filters.exact) {
          if (hashQueryKey(query.queryKey) !== hashQueryKey(filters.queryKey)) {
            return false
          }
        } else {
          // Partial match - filters.queryKey must be prefix of query.queryKey
          const filterKey = filters.queryKey
          if (filterKey.length > query.queryKey.length) return false
          for (let i = 0; i < filterKey.length; i++) {
            if (
              hashQueryKey([filterKey[i]]) !== hashQueryKey([query.queryKey[i]])
            ) {
              return false
            }
          }
        }
      }

      // Filter by stale
      if (filters.stale !== undefined) {
        const staleTime =
          query.options.staleTime ?? this.defaultOptions.staleTime ?? 0
        const isStale = query.isStale(staleTime)
        if (filters.stale !== isStale) return false
      }

      // Filter by fetchStatus
      if (
        filters.fetchStatus &&
        query.state.fetchStatus !== filters.fetchStatus
      ) {
        return false
      }

      // Filter by predicate
      if (filters.predicate && !filters.predicate(query)) {
        return false
      }

      return true
    })
  }

  private removeQueryFromDependencies(query: Query): void {
    const depends = query.options.depends ?? []

    for (const dep of depends) {
      this.unregisterQueryDependency(dep, query)
    }
    this.removeRuntimeQueryDependencies(query)
  }

  private replaceRuntimeQueryDependencies(
    query: Query,
    dependencyHashes: Set<string>,
  ): void {
    this.removeRuntimeQueryDependencies(query)
    this.runtimeQueryDependencies.set(query.queryHash, dependencyHashes)
    query.setQueryDependencies([...dependencyHashes].map(hash => {
      const dependency = this.queries.get(hash)
      if (!dependency) throw new Error('Batch query dependency missing from cache')
      return dependency
    }))
    for (const dependencyHash of dependencyHashes) {
      this.registerQueryDependency(queryHashDependency(dependencyHash), query)
    }
  }

  private removeRuntimeQueryDependencies(query: Query): void {
    const dependencyHashes = this.runtimeQueryDependencies.get(query.queryHash)
    if (!dependencyHashes) {
      return
    }
    for (const dependencyHash of dependencyHashes) {
      this.unregisterQueryDependency(queryHashDependency(dependencyHash), query)
    }
    this.runtimeQueryDependencies.delete(query.queryHash)
  }

  invalidateQueryDependents(query: Query): void {
    const dependents = this.dependencyAndQuery.get(
      queryHashDependency(query.queryHash),
    )
    if (!dependents) {
      return
    }
    for (const dependent of dependents) {
      if (dependent === query) continue
      void dependent.invalidate({ source: 'query' })
    }
  }

  invalidateMutationDependencies(
    invalidations: MutationInvalidation[],
    mutationId: string,
  ): void {
    const invalidated = new Set<Query>()
    for (const invalidation of invalidations) {
      for (const query of this.dependencyAndQuery.get(invalidation) ?? []) {
        if (invalidated.has(query)) continue
        invalidated.add(query)
        void query.invalidate({ source: 'mutation', mutationId })
      }
    }
  }

  private registerQueryDependency(dep: QueryDependency, query: Query): void {
    for (const key of queryDependencyKeys(dep)) {
      if (!this.dependencyAndQuery.has(key)) {
        this.dependencyAndQuery.set(key, new Set())
      }
      this.dependencyAndQuery.get(key)?.add(query)
    }
  }

  private unregisterQueryDependency(dep: QueryDependency, query: Query): void {
    for (const key of queryDependencyKeys(dep)) {
      const linkedQueries = this.dependencyAndQuery.get(key)
      if (!linkedQueries) continue
      linkedQueries.delete(query)
      if (linkedQueries.size === 0) {
        this.dependencyAndQuery.delete(key)
      }
    }
  }

  garbageCollectQuery(query: Query): void {
    const existing = this.queries.get(query.queryHash)
    if (!existing || existing !== query) {
      return
    }

    // Query became active again before timer fired
    if (existing.observers.length > 0) {
      return
    }

    if (this.logging) {
      console.info(toLogFmt({ queryKey: query.queryKey, GC: true }))
    }

    this.removeQueryFromDependencies(existing)
    this.queries.delete(existing.queryHash)
    existing.destroy()
  }

  clear(): void {
    for (const query of this.queries.values()) {
      query.destroy()
    }
    this.queries.clear()
    for (const mutation of this.mutations.values()) {
      mutation.destroy()
    }
    this.mutations.clear()
    this.dependencyAndQuery.clear()
  }

  destroy(): void {
    this.clear()
    if (this.windowFocusUnsubscribe) {
      this.windowFocusUnsubscribe()
      this.windowFocusUnsubscribe = undefined
    }
  }

  debug(): void {
    const queries = Array.from(this.queries.values())
    const mutations = Array.from(this.mutations.values())
    const lines: string[] = [`[QC] ${queries.length}q/${mutations.length}m`]

    for (const query of queries) {
      const keyStr = JSON.stringify(query.queryKey)
      const keyLines = keyStr.split('\\n')
      lines.push(`[q] ${keyLines[0]}`)
      for (let i = 1; i < keyLines.length; i++) {
        lines.push(`    ${keyLines[i]}`)
      }

      const status =
        query.state.status === 'success'
          ? '✓'
          : query.state.status === 'error'
            ? '✗'
            : '⏳'
      const staleTime =
        query.options.staleTime ?? this.defaultOptions.staleTime ?? 0
      const stale = query.isStale(staleTime) ? 'stale' : 'fresh'
      const fetchStatus = query.state.fetchStatus === 'fetching' ? 'fetch' : ''
      const parts = [status, stale, fetchStatus, `${query.observers.length}obs`]
        .filter(Boolean)
        .join(' ')
      lines.push(
        `    ${parts}`,
        `    data: ${JSON.stringify(query.state.data)}`,
      )
    }

    for (const mutation of mutations) {
      lines.push(
        `[m] ${mutation.id} | ${mutation.state.status} | fc=${mutation.state.failureCount}`,
        `    vars: ${JSON.stringify(mutation.state.variables)}`,
        `    data: ${JSON.stringify(mutation.state.data)}`,
      )
      if (mutation.state.error) {
        lines.push(`    err: ${JSON.stringify(mutation.state.error.message)}`)
      }
    }

    console.info(lines.join('\n'))
  }

  getQuery(queryKey: unknown[]): Query | undefined {
    const queryHash = this.getQueryHash(queryKey)
    return this.queries.get(queryHash)
  }

  /** TanStack `setQueryData`, limited to an existing query (one without options has no queryFn to refetch with). */
  setQueryData(queryKey: unknown[], data: QueryData): boolean {
    const query = this.getQuery(queryKey)
    query?.setData(data)
    return query !== undefined
  }

  getMutation(mutationId: string): Mutation | undefined {
    return this.mutations.get(mutationId)
  }

  removeMutation(mutationId: string): void {
    const mutation = this.mutations.get(mutationId)
    if (mutation) {
      if (this.logging) {
        console.info(toLogFmt({ mutationId, REMOVED: true }))
      }
      mutation.destroy()
      this.mutations.delete(mutationId)
    }
  }

  private mergeMutationOptions<TData = unknown, TVariables = unknown>(
    options: MutationOptions<TData, TVariables>,
  ): MutationOptions<TData, TVariables> {
    return {
      ...options,
      retry: options.retry ?? this.defaultMutationOptions.retry,
      retryDelay: options.retryDelay ?? this.defaultMutationOptions.retryDelay,
      onError:
        options.onError ??
        (this.defaultMutationOptions.onError as MutationOptions<
          TData,
          TVariables
        >['onError']),
    }
  }

  syncMutationOptions<TData = unknown, TVariables = unknown>(
    mutationId: string,
    options: MutationOptions<TData, TVariables>,
  ): Mutation<TData, TVariables> {
    let mutation = this.mutations.get(mutationId) as
      | Mutation<TData, TVariables>
      | undefined

    if (mutation) {
      // Compare options and update if changed
      const mergedNewOptions = this.mergeMutationOptions(options)
      const optionsChanged = !deepEqual(mutation.options, mergedNewOptions)

      if (optionsChanged) {
        mutation.options = mergedNewOptions
      }
    } else {
      const mergedOptions = this.mergeMutationOptions(options)
      mutation = new Mutation<TData, TVariables>(
        mutationId,
        mergedOptions,
        this,
      )
      this.mutations.set(mutationId, mutation as unknown as Mutation)
    }

    return mutation
  }

  addMutation<TData = unknown, TVariables = unknown>(
    mutationId: string,
    options: MutationOptions<TData, TVariables>,
  ): Mutation<TData, TVariables> {
    return this.syncMutationOptions(mutationId, options)
  }
}

function resolveInvalidations<TVariables>(
  invalidates: MutationOptionsBase<unknown, TVariables>['invalidates'],
  variables: TVariables,
): MutationInvalidation[] {
  if (!invalidates) return []
  const resolved =
    typeof invalidates === 'function' ? invalidates(variables) : invalidates
  return Array.isArray(resolved) ? resolved : [resolved]
}

function queryDependencyKeys(dep: QueryDependency): string[] {
  return [dep]
}

function queryHashDependency(queryHash: string): string {
  return `query:${queryHash}`
}
